import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Boxes, CalendarDays, Clock, Plus, AlertCircle } from "lucide-react";
import { PageHead } from "../components/PageHead";
import { DataTable } from "../components/DataTable";
import { EquipmentModal } from "../components/EquipmentModal";
import { DisciplineBadge, PortalBadge } from "../components/Badges";
import api from "../lib/api";
import { fmtDate, fmtNum } from "../lib/format";

const TABS = [["all", "All equipment"], ["active", "Active"], ["due", "Due"], ["out_of_service", "Unserviceable"]];

export default function MyEquipment() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all");
  const [stats, setStats] = useState({});
  const [upcoming, setUpcoming] = useState([]);
  const [sel, setSel] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/equipment/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/calibration-due", { params: { filter: "due_soon", days: 61, limit: 8 } })
      .then((r) => setUpcoming(r.data.items || [])).catch(() => {});
  }, [refresh]);

  const columns = [
    { key: "description", label: "Equipment", sortable: true, colSearch: true, render: (v, row) => <span className="cell-2" style={{ display: "inline-flex" }}><span className="t-strong">{v || "—"}</span><span className="s">{row.manufacturer || ""}</span></span> },
    { key: "serial_number", label: "Serial / issue", sortable: true, colSearch: true, mono: true, render: (v, row) => v || row.issue_number || "—" },
    { key: "discipline", label: "Discipline", sortable: true, colSearch: true, render: (v) => <DisciplineBadge name={v} /> },
    { key: "last_calibration_date", label: "Last cal.", sortable: true, mono: true, render: (v) => <span className="t-mut">{fmtDate(v)}</span> },
    { key: "calibration_due_date", label: "Next due", sortable: true, mono: true, render: (v, row) => row.scrapped ? <span style={{ color: "var(--text-3)", textDecoration: "line-through" }}>—</span> : <span style={{ fontWeight: 700 }}>{fmtDate(v)}</span> },
    { key: "portal_status", label: "Status", render: (v) => <PortalBadge status={v} /> },
  ];

  return (
    <>
      <PageHead crumb="My Account" title="My Equipment"
        sub="Every item you have ever registered with PTE — sortable & searchable. Click a row for full history."
        actions={<button className="btn gold" onClick={() => navigate("/app/request-quote")} data-testid="portal-request-quote"><Plus size={15} /> Request a quote</button>} />

      <div className="grid cols-4" style={{ marginBottom: 20 }}>
        <div className="card pad stat" data-testid="portal-stat-total"><div className="lab"><span className="ico b-green"><Boxes size={16} /></span>Total equipment</div><div className="num">{fmtNum(stats.all)}</div></div>
        <div className="card pad stat" data-testid="portal-stat-active"><div className="lab"><span className="ico b-blue"><Boxes size={16} /></span>Active</div><div className="num">{fmtNum(stats.active)}</div></div>
        <div className="card pad stat" data-testid="portal-stat-due"><div className="lab"><span className="ico b-amber"><CalendarDays size={16} /></span>Due (≤61 days + overdue)</div><div className="num">{fmtNum(stats.due)}</div></div>
        <div className="card pad stat" data-testid="portal-stat-oos"><div className="lab"><span className="ico b-red"><Clock size={16} /></span>Unserviceable</div><div className="num">{fmtNum(stats.out_of_service)}</div></div>
      </div>

      <div className="card pad" style={{ marginBottom: 20 }} data-testid="portal-coming-up">
        <div className="sec-h" style={{ marginTop: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <h3 style={{ fontSize: 14, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
            <AlertCircle size={15} style={{ color: "var(--amber)" }} /> Coming up — next 61 days
          </h3>
          <button className="btn ghost" data-testid="portal-due-viewall" onClick={() => setTab("due")} style={{ fontSize: 12 }}>View all due</button>
        </div>
        {upcoming.length === 0 ? (
          <div className="t-mut" style={{ fontSize: 13, padding: "10px 2px" }}>Nothing due in the next 61 days — you're all caught up.</div>
        ) : (
          <div className="tbl-wrap" style={{ marginTop: 6 }}>
            <table>
              <thead><tr><th>Equipment</th><th>Serial / issue</th><th>Discipline</th><th>Due date</th><th style={{ textAlign: "right" }}>Countdown</th></tr></thead>
              <tbody>
                {upcoming.map((row) => {
                  const d = row.days_remaining;
                  const colour = d == null ? "var(--text-2)" : d < 0 ? "var(--red)" : d <= 7 ? "var(--amber)" : "var(--text-2)";
                  return (
                    <tr key={row.id} onClick={() => setSel(row.id)} style={{ cursor: "pointer" }} data-testid="portal-due-row">
                      <td className="t-strong">{row.description || "—"}</td>
                      <td className="mono t-mut">{row.serial_number || row.issue_number || "—"}</td>
                      <td><DisciplineBadge name={row.discipline} /></td>
                      <td className="mono t-strong">{fmtDate(row.calibration_due_date)}</td>
                      <td className="mono" style={{ textAlign: "right", fontWeight: 700, color: colour }}>
                        {d == null ? "—" : d < 0 ? `${Math.abs(d)}d overdue` : `${d}d`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <DataTable endpoint="/equipment" params={{ status_filter: tab === "all" ? undefined : tab }} columns={columns} rowKey="id" testid="portal-equipment"
        defaultSort={{ by: "calibration_due_date", dir: "asc" }} searchPlaceholder="Search your equipment…"
        onRowClick={(row) => setSel(row.id)} refreshSignal={refresh}
        toolbar={TABS.map(([k, l]) => <button key={k} className={`ftab ${tab === k ? "active" : ""}`} data-testid={`portal-tab-${k}`} onClick={() => setTab(k)}>{l}{stats[k] != null && <span className="c">{fmtNum(stats[k])}</span>}</button>)} />

      {sel && <EquipmentModal id={sel} canEdit={false} onClose={() => setSel(null)} onChanged={() => setRefresh((x) => x + 1)} />}
    </>
  );
}
